import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformService } from '../services/platformService';
import type { 
  PlatformListParams, 
  CreatePlatformData,
  ClientListParams,
  CreateClientData,
  PlatformConfiguration
} from '../services/platformService';
import type { EditPlatformData } from '../components/platforms/EditPlatformModal';

// Query keys for React Query
export const platformKeys = {
  all: ['platforms'] as const,
  lists: () => [...platformKeys.all, 'list'] as const,
  list: (params: PlatformListParams) => [...platformKeys.lists(), params] as const,
  details: () => [...platformKeys.all, 'detail'] as const,
  detail: (id: string) => [...platformKeys.details(), id] as const,
  clients: (platformId: string) => [...platformKeys.detail(platformId), 'clients'] as const,
  clientsList: (platformId: string, params: ClientListParams) => 
    [...platformKeys.clients(platformId), params] as const,
  configurations: (platformId: string) => [...platformKeys.detail(platformId), 'configurations'] as const,
};

// Platform listing hook
export const usePlatforms = (params: PlatformListParams = {}) => {
  return useQuery({
    queryKey: platformKeys.list(params),
    queryFn: () => platformService.listPlatforms(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Single platform hook
export const usePlatform = (id: string) => {
  return useQuery({
    queryKey: platformKeys.detail(id),
    queryFn: () => platformService.getPlatform(id),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Platform clients hook
export const usePlatformClients = (platformId: string, params: ClientListParams = {}) => {
  return useQuery({
    queryKey: platformKeys.clientsList(platformId, params),
    queryFn: () => platformService.listPlatformClients(platformId, params),
    enabled: Boolean(platformId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Platform configurations hook
export const usePlatformConfigurations = (platformId: string) => {
  return useQuery({
    queryKey: platformKeys.configurations(platformId),
    queryFn: () => platformService.listPlatformConfigurations(platformId),
    enabled: Boolean(platformId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Create platform mutation
export const useCreatePlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlatformData) => platformService.createPlatform(data),
    onSuccess: (response, _variables) => {
      // Invalidate and refetch platform lists
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
      
      // Add the new platform to the cache
      queryClient.setQueryData(
        platformKeys.detail(response.platform.id),
        response.platform
      );
    },
    onError: (error) => {
      console.error('Failed to create platform:', error);
    },
  });
};

// Update platform mutation
export const useUpdatePlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditPlatformData | Partial<CreatePlatformData> }) =>
      platformService.updatePlatform(id, data),
    onSuccess: (updatedPlatform, variables) => {
      // Update the platform in cache
      queryClient.setQueryData(
        platformKeys.detail(variables.id),
        updatedPlatform
      );
      
      // Invalidate platform lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to update platform:', error);
    },
  });
};

// Delete platform mutation
export const useDeletePlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => platformService.deletePlatform(id),
    onSuccess: (_, deletedId) => {
      // Remove the platform from cache
      queryClient.removeQueries({ queryKey: platformKeys.detail(deletedId) });
      
      // Invalidate platform lists
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete platform:', error);
    },
  });
};

// Create platform client mutation
export const useCreatePlatformClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ platformId, data }: { platformId: string; data: CreateClientData }) =>
      platformService.createPlatformClient(platformId, data),
    onSuccess: (_response, variables) => {
      // Invalidate clients list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.clients(variables.platformId) 
      });
      
      // Also invalidate platform details to update client count
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.detail(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to create platform client:', error);
    },
  });
};

// Update platform client mutation
export const useUpdatePlatformClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      platformId, 
      clientId, 
      data 
    }: { 
      platformId: string; 
      clientId: string; 
      data: Partial<CreateClientData> 
    }) => platformService.updatePlatformClient(platformId, clientId, data),
    onSuccess: (_updatedClient, variables) => {
      // Invalidate clients list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.clients(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to update platform client:', error);
    },
  });
};

// Delete platform client mutation
export const useDeletePlatformClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ platformId, clientId }: { platformId: string; clientId: string }) =>
      platformService.deletePlatformClient(platformId, clientId),
    onSuccess: (_, variables) => {
      // Invalidate clients list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.clients(variables.platformId) 
      });
      
      // Also invalidate platform details to update client count
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.detail(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to delete platform client:', error);
    },
  });
};

// Platform configurations mutations
export const useCreatePlatformConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      platformId, 
      data 
    }: { 
      platformId: string; 
      data: Partial<PlatformConfiguration> 
    }) => platformService.createPlatformConfiguration(platformId, data),
    onSuccess: (_, variables) => {
      // Invalidate configurations list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.configurations(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to create platform configuration:', error);
    },
  });
};

export const useUpdatePlatformConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      platformId, 
      configId, 
      data 
    }: { 
      platformId: string; 
      configId: string; 
      data: Partial<PlatformConfiguration> 
    }) => platformService.updatePlatformConfiguration(platformId, configId, data),
    onSuccess: (_, variables) => {
      // Invalidate configurations list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.configurations(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to update platform configuration:', error);
    },
  });
};

export const useDeletePlatformConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ platformId, configId }: { platformId: string; configId: string }) =>
      platformService.deletePlatformConfiguration(platformId, configId),
    onSuccess: (_, variables) => {
      // Invalidate configurations list for this platform
      queryClient.invalidateQueries({ 
        queryKey: platformKeys.configurations(variables.platformId) 
      });
    },
    onError: (error) => {
      console.error('Failed to delete platform configuration:', error);
    },
  });
}; 